///
/// EarthBrowser - Wildfire Browser
///
/// Movable window for browsing live wildfire perimeters using local sync data
///
/// Created by Matt Giger on 9/5/2026
/// Copyright (c) 2026 Geodata Labs
///

import { useCallback, useMemo, useState, useEffect, memo, useRef } from 'react'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import { sendDatasetAction } from '@/redux/earthBrowserSlice'
import { DatasetBrowserWindow } from '@/components/windows/DatasetBrowserWindow'
import { DatasetVisibilityToggle } from '@/components/windows/DatasetVisibilityToggle'
import { DataBrowserView, type DataBrowserViewHandle } from '@/components/data-browser/DataBrowserView'
import { useRegisterWindowContent } from '@/components/windows/WindowContentRegistry'
import { DataProviderRegistry } from '@/data-providers/DataProviderRegistry'
import { WildfireDataProvider } from '@/data-providers/WildfireDataProvider'
import { WildfiresTable, type WildfireProperties } from '@/earthbrowser/dataset-distribution/wildfires/WildfiresTable'
import { useDatasetLayerRefresh } from '@/hooks/useDatasetLayerRefresh'
import type { DataRow } from '@/data-providers/types'
import { Flame } from 'lucide-react'
import { isMobile } from '@/earthbrowser/utils/platform'
import { closeDatasetWindow, type DatasetBrowserWindowState } from '@/redux/dialogSlice'

interface WildfireBrowserRendererProps {
	windowId: string
	windowState: DatasetBrowserWindowState
}

export const WildfireBrowserRenderer = memo(function WildfireBrowserRenderer({
	windowId,
	windowState,
}: WildfireBrowserRendererProps) {
	const dispatch = useAppDispatch()
	const visibleDatasets = useAppSelector(state => state.earthbrowser.visibleDatasets)
	const wildfiresVisible = visibleDatasets.includes("wildfire")

	// Get singleton scene manager from Redux
	const layerManager = useAppSelector(state => state.datasetScene.layerManager)

	// Local state
	const [isInitialized, setIsInitialized] = useState(false)
	const [isLoading, setIsLoading] = useState(true)
	const [wildfires, setWildfires] = useState<WildfireProperties[]>([])
	const [totalCount, setTotalCount] = useState(0)
	const [timeUpdateTrigger, setTimeUpdateTrigger] = useState(0)
	const dataBrowserViewRef = useRef<DataBrowserViewHandle>(null)

	// Load wildfires from the local table (mount, sync refresh, manual refresh)
	const loadWildfires = useCallback(async (showLoading: boolean) => {
		if (showLoading) setIsLoading(true)
		try {
			const loadedWildfires = await WildfiresTable.getAll()
			setWildfires(loadedWildfires)
			setTimeUpdateTrigger(prev => prev + 1)

			const count = await WildfiresTable.getCount()
			setTotalCount(count)
		} catch (error) {
			console.error('Failed to load wildfires:', error)
		} finally {
			if (showLoading) setIsLoading(false)
		}
	}, [])

	// Load wildfires on mount
	useEffect(() => {
		void loadWildfires(true)
	}, [loadWildfires])

	// Reload silently whenever the wildfires layer refreshes after a sync,
	// so the table doesn't show a stale snapshot from when the window opened
	useDatasetLayerRefresh('wildfires', () => {
		void loadWildfires(false)
	})

	// Initialize and update data provider
	useEffect(() => {
		if (isLoading) return

		if (!isInitialized) {
			const oldProvider = DataProviderRegistry.get(windowId)
			if (oldProvider) {
				void oldProvider.dispose?.()
				DataProviderRegistry.unregister(windowId)
			}

			const provider = new WildfireDataProvider({
				id: windowId,
				name: 'Wildfires',
				queryFn: async () => wildfires // Initial data
			})

			void provider.initialize().then(() => {
				DataProviderRegistry.register(windowId, provider)
				setIsInitialized(true)
			})
		} else {
			const provider = DataProviderRegistry.get(windowId) as WildfireDataProvider | undefined
			if (provider) {
				provider.updateCachedData(wildfires)
			}
		}
	}, [wildfires, windowId, isLoading, isInitialized])

	// Timer to update relative times every minute (synced to minute boundary)
	useEffect(() => {
		let interval: NodeJS.Timeout | null = null

		const now = new Date()
		const msUntilNextMinute = (60 - now.getSeconds()) * 1000 - now.getMilliseconds()

		const timeout = setTimeout(() => {
			setTimeUpdateTrigger(prev => prev + 1)
			interval = setInterval(() => {
				setTimeUpdateTrigger(prev => prev + 1)
			}, 60000)
		}, msUntilNextMinute)

		return () => {
			clearTimeout(timeout)
			if (interval) {
				clearInterval(interval)
			}
		}
	}, [])

	// Sync scene layer visibility with Redux state
	useEffect(() => {
		if (layerManager) {
			layerManager.setLayerVisible('wildfires', wildfiresVisible)
		}
	}, [layerManager, wildfiresVisible])

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			const provider = DataProviderRegistry.get(windowId)
			if (provider) {
				void provider.dispose?.()
				DataProviderRegistry.unregister(windowId)
			}
		}
	}, [windowId])

	// Handle wildfire row click: focus flies the camera to frame the perimeter
	const handleRowClick = useCallback((row: DataRow) => {
		const wildfire = row as unknown as WildfireProperties
		const featureId = wildfire.id

		// Ensure wildfires are visible on the map
		if (!wildfiresVisible) {
			dispatch(sendDatasetAction({
				type: "wildfire",
				action: "enable"
			}))
			if (layerManager) {
				layerManager.setLayerVisible('wildfires', true)
			}
		}

		if (layerManager) {
			layerManager.focusSceneObject('wildfires', featureId)
		}

		if (isMobile()) {
			dispatch(closeDatasetWindow(windowId))
		}
	}, [layerManager, wildfiresVisible, dispatch, windowId])

	// Handle refresh
	const handleRefresh = useCallback(() => {
		void loadWildfires(true)
		if (layerManager) {
			void layerManager.refreshLayer('wildfires')
		}
	}, [loadWildfires, layerManager])

	// Content
	const content = useMemo(() => {
		const provider = DataProviderRegistry.get(windowId)

		if (provider && isInitialized) {
			return (
				<DataBrowserView
					ref={dataBrowserViewRef}
					key={`wildfire-browser-${wildfires.length}-${timeUpdateTrigger}`}
					provider={provider}
					onRowClick={handleRowClick}
					onRefresh={handleRefresh}
					totalDatabaseCount={totalCount}
					searchPlaceholder="Search by fire name, state, cause..."
				/>
			)
		}

		return (
			<div className="flex flex-col items-center justify-center h-full bg-black/20 backdrop-blur-sm">
				<div className="flex flex-col items-center gap-4 p-8">
					<div className="relative w-16 h-16">
						<div className="absolute inset-0 border-4 border-rose-500/20 rounded-full" />
						<div className="absolute inset-0 border-4 border-transparent border-t-rose-500 rounded-full animate-spin" />
					</div>
					<p className="text-base font-medium text-white">
						Loading Wildfires
					</p>
					<p className="text-sm text-white/60">
						Initializing...
					</p>
				</div>
			</div>
		)
	}, [windowId, isInitialized, handleRowClick, handleRefresh, totalCount, wildfires.length, timeUpdateTrigger])

	// Register content with the window content registry for drawer docking
	const contentRenderer = useCallback(() => content, [content])
	useRegisterWindowContent(windowId, contentRenderer)

	// Handle window being brought to front - focus search input
	const handleBringToFront = useCallback(() => {
		dataBrowserViewRef.current?.focusSearch()
	}, [])

	return (
		<DatasetBrowserWindow
			windowState={windowState}
			icon={<Flame className="w-5 h-5" />}
			iconColor="text-rose-400"
			titlebarPrefix={<DatasetVisibilityToggle windowType="wildfire" />}
			onBringToFront={handleBringToFront}
		>
			{content}
		</DatasetBrowserWindow>
	)
})
